import { ImmomioIconButtonComponent } from '@immomio/ui/button';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
  TemplateRef,
  viewChild,
  contentChild
} from '@angular/core';

import {
  DropdownStepperChildren,
  DropdownStepperParent
} from '@ui/shared/model';
import {
  NgbDropdown,
  NgbDropdownMenu,
  NgbDropdownToggle
} from '@ng-bootstrap/ng-bootstrap';

import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { AppFormFieldControl } from '../../legacy/form/form-field/form-field-control/form-field-control';
import { BaseControl } from '../../legacy/form/controls/base-control';
import { IsValueSelectedInDropdownStepperPipe } from './is-value-selected.pipe';

@Component({
  selector: 'app-multi-select-dropdown-stepper',
  templateUrl: './multi-select-dropdown-stepper.component.html',
  styleUrl: './multi-select-dropdown-stepper.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectDropdownStepperComponent),
      multi: true
    },
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => MultiSelectDropdownStepperComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgTemplateOutlet,
    NgbDropdownMenu,
    TranslatePipe,
    IsValueSelectedInDropdownStepperPipe,
    ImmomioIconButtonComponent
  ]
})
export class MultiSelectDropdownStepperComponent<T> extends BaseControl<T[]> {
  private static nextId = 0;
  protected readonly idPrefix = `multi-select-dropdown-stepper-${MultiSelectDropdownStepperComponent.nextId++}`;
  items = input.required<DropdownStepperParent<T>[]>();
  dropdownTitle = input.required<string>();
  placeholderText = input.required<string>();
  multipleItemsSelectedText = input.required<string>();
  resetChildrenOnNewParentSelection = input<boolean>(false);
  readonly dropdown = viewChild(NgbDropdown);
  readonly templateRef = contentChild(TemplateRef);

  selectedParent = signal<DropdownStepperParent<T> | undefined | null>(
    undefined
  );
  selectedChildren = signal<DropdownStepperChildren<T>[] | undefined | null>(
    []
  );
  animationState = 'showParents';
  isTransitioningToManufacturers = false;
  isDropdownOpened = false;

  // Used in combination with resetChildrenOnNewParentSelection
  overwriteChildrenOnNextSelection = false;

  public writeValue(value?: T[]): void {
    super.writeValue(value);
    const selectedChildren = this.findValueInItems(value);
    if (selectedChildren) this.selectedChildren.set(selectedChildren);
  }

  set value(value: T[]) {
    super.value = value;
    const selectedChild = this.findValueInItems(value);
    if (selectedChild) this.selectedChildren.set(selectedChild);
  }

  handleDropdownOpenChange(opened: boolean) {
    if (!opened) this.animationState = 'showParents';
    this.isDropdownOpened = opened;
  }

  selectParent(parent: DropdownStepperParent<T>): void {
    if (this.isTransitioningToManufacturers) return;

    this.animationState = 'showChildren';
    if (parent === this.selectedParent()) {
      return;
    } else if (this.resetChildrenOnNewParentSelection) {
      this.overwriteChildrenOnNextSelection = true;
    }
    this.selectedParent.set(parent);
  }

  selectChild(child: DropdownStepperChildren<T>): void {
    if (this.overwriteChildrenOnNextSelection) {
      if (this.selectedChildren()?.length)
        this.selectedChildren.update(selectedChildren =>
          selectedChildren.filter(child =>
            this.selectedParent().items.some(
              parentChild => parentChild.value === child.value
            )
          )
        );
      this.overwriteChildrenOnNextSelection = false;
    }
    if (
      this.selectedChildren().some(
        selectedChild => selectedChild.value === child.value
      )
    ) {
      this.selectedChildren.update(v =>
        v.filter(selectedChild => selectedChild.value !== child.value)
      );
    } else {
      this.selectedChildren.update(v => [...v, child]);
    }

    this.value = this.selectedChildren().map(child => child.value);
  }

  backToParents(): void {
    this.isTransitioningToManufacturers = true;
    setTimeout(() => {
      // After the animation has finished we want to reset the selected parent
      // If we do this immediately the user would see that the child list looks broken because it's empty
      this.selectedParent.set(undefined);
      this.isTransitioningToManufacturers = false;
    }, 375);
    this.animationState = 'showParents';
  }

  clear(event?: Event): void {
    // When you don't have this, then the dropdown would be opened/closed
    // It stops the parent element from also receiving the click event
    if (event) event.stopPropagation();
    this.value = [];
    this.selectedChildren.set([]);
  }

  private findValueInItems(values: T[]): DropdownStepperChildren<T>[] {
    const children: DropdownStepperChildren<T>[] = [];
    const fl = this.items().flatMap(parent => parent.items);

    values.forEach(value => {
      const child = fl.find(child => child.value === value);
      if (child) children.push(child);
    });

    return children;
  }
}
